/**
 * Production-Grade Email & Username Validation Engines
 * Implements RFC 5321/5322 specifications with practical web-security constraints
 * and strict username formatting, reserved words, and abuse filtering.
 */

export interface ValidationResult {
  isValid: boolean;
  error?: string;
  normalized?: string;
}

export interface EmailValidationResult extends ValidationResult {
  normalizedEmail?: string;
}

export interface UsernameValidationResult extends ValidationResult {
  normalizedUsername?: string;
}

// ---------------------------------------------------------------------------
// Email Validation
// ---------------------------------------------------------------------------

const LOCAL_PART_ALLOWED_CHARS = /^[a-zA-Z0-9!#$%&'*+\-/=?^_`{|}~.]+$/;
const DOMAIN_LABEL_ALLOWED_CHARS = /^[a-zA-Z0-9-]+$/;
const TLD_ALLOWED_CHARS = /^[a-zA-Z]{2,}$/;

/**
 * Validates an email address according to RFC 5321/5322 specifications with
 * production-grade web application security constraints.
 */
export function validateEmail(emailInput: string | null | undefined): EmailValidationResult {
  if (!emailInput || typeof emailInput !== 'string') {
    return { isValid: false, error: 'Email address is required.' };
  }

  const raw = emailInput.trim();
  if (raw.length === 0) {
    return { isValid: false, error: 'Email address is required.' };
  }

  // 1. Overall Format & Structure
  if (raw.length > 254) {
    return {
      isValid: false,
      error: 'Email address must not exceed 254 characters.',
    };
  }

  // Quoted local parts rejected
  if (raw.startsWith('"') || raw.includes('"@') || raw.includes('"')) {
    return {
      isValid: false,
      error: 'Quoted local-part email addresses are not permitted.',
    };
  }

  if (/\s/.test(raw)) {
    return {
      isValid: false,
      error: 'Email address cannot contain whitespace.',
    };
  }

  const atIndex = raw.indexOf('@');
  if (atIndex === -1) {
    return {
      isValid: false,
      error: "Email address must contain an '@' symbol separating local-part and domain.",
    };
  }

  if (raw.indexOf('@', atIndex + 1) !== -1) {
    return {
      isValid: false,
      error: "Email address cannot contain multiple '@' symbols.",
    };
  }

  const localPart = raw.substring(0, atIndex);
  const domain = raw.substring(atIndex + 1);

  // 2. Local-Part Rules (Before the @)
  if (localPart.length === 0) {
    return {
      isValid: false,
      error: 'Email local-part (before @) must not be empty.',
    };
  }

  if (localPart.length > 64) {
    return {
      isValid: false,
      error: 'Email local-part (before @) must not exceed 64 characters.',
    };
  }

  if (!LOCAL_PART_ALLOWED_CHARS.test(localPart)) {
    return {
      isValid: false,
      error: 'Email local-part contains invalid characters.',
    };
  }

  if (localPart.startsWith('.')) {
    return {
      isValid: false,
      error: "Email local-part cannot start with a period ('.').",
    };
  }

  if (localPart.endsWith('.')) {
    return {
      isValid: false,
      error: "Email local-part cannot end with a period ('.').",
    };
  }

  if (localPart.includes('..')) {
    return {
      isValid: false,
      error: "Email local-part cannot contain consecutive periods ('..').",
    };
  }

  // 3. Domain Rules (After the @)
  if (domain.length === 0) {
    return {
      isValid: false,
      error: 'Email domain (after @) must not be empty.',
    };
  }

  if (domain.length > 255) {
    return {
      isValid: false,
      error: 'Email domain must not exceed 255 characters.',
    };
  }

  // Rejection of IP literal addresses (e.g. [192.168.1.1])
  if (domain.startsWith('[') || domain.endsWith(']')) {
    return {
      isValid: false,
      error: 'IP literal email addresses are not permitted.',
    };
  }

  const labels = domain.split('.');
  if (labels.length < 2) {
    return {
      isValid: false,
      error: "Email domain must contain at least one dot separating labels (e.g., 'example.com').",
    };
  }

  for (let i = 0; i < labels.length; i++) {
    const label = labels[i];
    if (label.length === 0) {
      return {
        isValid: false,
        error: "Email domain contains empty label or consecutive dots ('..').",
      };
    }

    if (label.length > 63) {
      return {
        isValid: false,
        error: `Email domain label '${label}' exceeds 63 characters.`,
      };
    }

    if (!DOMAIN_LABEL_ALLOWED_CHARS.test(label)) {
      return {
        isValid: false,
        error: `Email domain label '${label}' contains invalid characters.`,
      };
    }

    if (label.startsWith('-')) {
      return {
        isValid: false,
        error: `Email domain label '${label}' cannot start with a hyphen ('-').`,
      };
    }

    if (label.endsWith('-')) {
      return {
        isValid: false,
        error: `Email domain label '${label}' cannot end with a hyphen ('-').`,
      };
    }
  }

  const tld = labels[labels.length - 1];
  if (!TLD_ALLOWED_CHARS.test(tld)) {
    return {
      isValid: false,
      error: 'Top-level domain (TLD) must be purely alphabetic and at least 2 characters long.',
    };
  }

  const normalizedEmail = raw.toLowerCase();
  return {
    isValid: true,
    normalized: normalizedEmail,
    normalizedEmail,
  };
}

// ---------------------------------------------------------------------------
// Username Validation
// ---------------------------------------------------------------------------

const USERNAME_ALLOWED_CHARS = /^[a-zA-Z0-9._-]+$/;
const IS_ALPHANUMERIC = /^[a-zA-Z0-9]$/;
const SPECIAL_CHARS = new Set(['.', '_', '-']);

export const RESERVED_USERNAMES = new Set([
  'admin',
  'administrator',
  'root',
  'system',
  'support',
  'help',
  'moderator',
  'mod',
  'api',
  'login',
  'logout',
  'signin',
  'signup',
  'register',
  'settings',
  'profile',
  'dashboard',
  'auth',
  'null',
  'undefined',
]);

export const BLOCKED_WORDS = [
  'anal',
  'anus',
  'asshole',
  'bastard',
  'bitch',
  'blowjob',
  'bollocks',
  'boner',
  'cock',
  'cunt',
  'dick',
  'dildo',
  'fag',
  'faggot',
  'fuck',
  'homo',
  'jizz',
  'kike',
  'nigger',
  'nigga',
  'penis',
  'piss',
  'pussy',
  'retard',
  'scrotum',
  'shit',
  'slut',
  'twat',
  'vagina',
  'wank',
  'whore',
];

/**
 * Normalizes leetspeak and symbol substitutions to standard latin characters
 * for profanity and abuse detection.
 */
export function normalizeLeetspeak(str: string): string {
  return str
    .toLowerCase()
    .replace(/[@4]/g, 'a')
    .replace(/[1!|]/g, 'i')
    .replace(/[0]/g, 'o')
    .replace(/[$5]/g, 's')
    .replace(/[3]/g, 'e')
    .replace(/[7+]/g, 't')
    .replace(/[^a-z0-9]/g, '');
}

/**
 * Checks if a string contains blocked profanity or abusive terms.
 */
export function containsProfanity(str: string): boolean {
  const normalized = normalizeLeetspeak(str);
  for (const word of BLOCKED_WORDS) {
    if (normalized.includes(word)) {
      return true;
    }
  }
  return false;
}

/**
 * Evaluates whether a user-submitted string is an acceptable, secure, and
 * well-formatted username.
 */
export function validateUsername(
  usernameInput: string | null | undefined
): UsernameValidationResult {
  if (!usernameInput || typeof usernameInput !== 'string') {
    return { isValid: false, error: 'Username is required.' };
  }

  const raw = usernameInput.trim();
  if (raw.length === 0) {
    return { isValid: false, error: 'Username is required.' };
  }

  // 1. Length
  if (raw.length < 3 || raw.length > 30) {
    return {
      isValid: false,
      error: 'Username must be between 3 and 30 characters in length.',
    };
  }

  // Whitespace check
  if (/\s/.test(usernameInput)) {
    return {
      isValid: false,
      error: 'Username cannot contain whitespace.',
    };
  }

  // 2. Allowed Characters
  if (!USERNAME_ALLOWED_CHARS.test(raw)) {
    return {
      isValid: false,
      error:
        'Username can only contain letters, numbers, underscores (_), hyphens (-), and periods (.).',
    };
  }

  // 3. Position & Sequence Constraints
  const firstChar = raw[0];
  const lastChar = raw[raw.length - 1];

  if (!IS_ALPHANUMERIC.test(firstChar)) {
    return {
      isValid: false,
      error: "Username must start with a letter or digit (cannot start with '.', '_', or '-').",
    };
  }

  if (!IS_ALPHANUMERIC.test(lastChar)) {
    return {
      isValid: false,
      error: "Username must end with a letter or digit (cannot end with '.', '_', or '-').",
    };
  }

  // Check consecutive special characters
  for (let i = 0; i < raw.length - 1; i++) {
    const current = raw[i];
    const next = raw[i + 1];
    if (SPECIAL_CHARS.has(current) && SPECIAL_CHARS.has(next)) {
      return {
        isValid: false,
        error: `Username cannot contain consecutive special characters ('${current}${next}').`,
      };
    }
  }

  // 4. Reserved Names & System Routing Collisions
  const lower = raw.toLowerCase();
  if (RESERVED_USERNAMES.has(lower)) {
    return {
      isValid: false,
      error: `Username '${raw}' is a reserved system or routing term.`,
    };
  }

  // 5. Profanity & Abuse
  if (containsProfanity(raw)) {
    return {
      isValid: false,
      error: 'Username contains inappropriate or prohibited terms.',
    };
  }

  return {
    isValid: true,
    normalized: raw,
    normalizedUsername: raw,
  };
}
